import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UploadCloud, ImagePlus, X } from "lucide-react";
import { PHOTO } from "@/constants/testIds";

export default function UploadDialog({ open, onOpenChange, defaultPetId, onUploaded }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState("");
  const [petId, setPetId] = useState(defaultPetId || "none");
  const [album, setAlbum] = useState("");
  const [pets, setPets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      api.get("/pets").then(({ data }) => setPets(data)).catch(() => {});
      setPetId(defaultPetId || "none");
      setFile(null);
      setPreview(null);
      setCaption("");
      setAlbum("");
    }
  }, [open, defaultPetId]);

  const handleFile = (f) => {
    if (!f) return;
    if (!/^image\/(jpe?g|png|webp|gif)$/i.test(f.type)) {
      toast.error("Only JPG, PNG, WEBP or GIF allowed");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please choose a photo first");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    if (caption) fd.append("caption", caption);
    if (petId && petId !== "none") fd.append("pet_id", petId);
    if (album) fd.append("album", album);
    try {
      const { data } = await api.post("/photos/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo saved! Analyzing breed...");
      onUploaded?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-3xl border-[#EAE2D6] bg-[#FDFBF7] p-0 overflow-hidden">
        <div className="p-6 sm:p-8">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-[#2D232E]">
              Add a new memory
            </DialogTitle>
            <DialogDescription className="font-hand text-lg text-[#E76F51]">
              drag &amp; drop or click to choose
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="mt-5 space-y-4">
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                handleFile(e.dataTransfer.files?.[0]);
              }}
              className={`relative cursor-pointer rounded-2xl border-2 border-dashed transition-all p-6 flex flex-col items-center justify-center min-h-[200px] ${
                dragOver
                  ? "border-[#E76F51] bg-[#E76F51]/5"
                  : "border-[#EAE2D6] bg-white hover:border-[#F4A261]"
              }`}
            >
              {preview ? (
                <>
                  <img src={preview} alt="preview" className="max-h-48 rounded-xl object-contain" />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setFile(null); setPreview(null); }}
                    className="absolute top-2 right-2 bg-white/90 border border-[#EAE2D6] rounded-full p-1.5 hover:bg-white"
                  >
                    <X className="w-4 h-4 text-[#2D232E]" />
                  </button>
                </>
              ) : (
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full bg-[#E76F51]/10 flex items-center justify-center mx-auto mb-3">
                    <UploadCloud className="w-7 h-7 text-[#E76F51]" />
                  </div>
                  <p className="text-[#2D232E] font-semibold">Drop a photo here</p>
                  <p className="text-sm text-[#6A606C] mt-1">or click to choose · JPG, PNG, WEBP · up to 15MB</p>
                </div>
              )}
              <input
                data-testid={PHOTO.uploadInput}
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </div>

            <div>
              <Label className="text-[#6A606C] mb-1.5 block">Caption</Label>
              <Input
                data-testid={PHOTO.captionInput}
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Sunday nap champion 💤"
                className="rounded-xl h-11 bg-white border-[#EAE2D6] focus-visible:ring-2 focus-visible:ring-[#E76F51]/40"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#6A606C] mb-1.5 block">Pet</Label>
                <Select value={petId} onValueChange={setPetId}>
                  <SelectTrigger data-testid={PHOTO.petSelect} className="rounded-xl h-11 bg-white border-[#EAE2D6]">
                    <SelectValue placeholder="Which pet?" />
                  </SelectTrigger>
                  <SelectContent className="rounded-2xl bg-white border-[#EAE2D6]">
                    <SelectItem value="none">— None —</SelectItem>
                    {pets.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#6A606C] mb-1.5 block">Album</Label>
                <Input
                  data-testid={PHOTO.albumInput}
                  value={album}
                  onChange={(e) => setAlbum(e.target.value)}
                  placeholder="e.g. Beach trip"
                  className="rounded-xl h-11 bg-white border-[#EAE2D6]"
                />
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                className="rounded-full text-[#6A606C]"
              >
                Cancel
              </Button>
              <Button
                data-testid={PHOTO.uploadSubmit}
                type="submit"
                disabled={busy || !file}
                className="rounded-full bg-[#E76F51] hover:bg-[#F4A261] text-white font-semibold px-6 shadow-[0_6px_20px_rgba(231,111,81,0.25)] transition-transform hover:scale-105"
              >
                <ImagePlus className="w-4 h-4 mr-1.5" />
                {busy ? "Saving..." : "Save memory"}
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
